import { TriangleAlert } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';
import { ConfirmationDialog } from '../../components/dialog/confirmation-dialog.tsx';
import { DialogStateManager } from '../../components/dialog/dialog-provider.tsx';

type UseDiscardChangesWarningResult = {
    confirmDiscard: (onDiscard: () => void, onKeepEditing?: () => void) => void;
};

export function useDiscardChangesWarning(isDirty: boolean): UseDiscardChangesWarningResult {
    const { t } = useTranslation('common');

    // Leaving the app only: in-app navigation unloads no document, so this never fires.
    useEffect(() => {
        if (!isDirty) {
            return;
        }

        const handler = (event: BeforeUnloadEvent): void => {
            event.preventDefault();
        };

        window.addEventListener('beforeunload', handler);

        return () => {
            window.removeEventListener('beforeunload', handler);
        };
    }, [isDirty]);

    const confirmDiscard = useCallback(
        (onDiscard: () => void, onKeepEditing?: () => void) => {
            if (!isDirty) {
                onDiscard();
                return;
            }

            void DialogStateManager.show(ConfirmationDialog, {
                focus: 'destroy',
                icon: TriangleAlert,
                title: t('discard_changes__title'),
                description: t('discard_changes__description'),
                accept: {
                    text: t('discard_changes__confirm'),
                    action: onDiscard,
                },
                reject: {
                    text: t('form__cancel'),
                    action: onKeepEditing,
                },
            });
        },
        [isDirty, t],
    );

    // The in-app counterpart, caught at the router. Pops only, so a link the form follows
    // still navigates.
    const blocker = useBlocker(
        useCallback(
            ({ historyAction }: { historyAction: string }) => isDirty && historyAction === 'POP',
            [isDirty],
        ),
    );

    useEffect(() => {
        if (blocker.state !== 'blocked') {
            return;
        }
        confirmDiscard(
            () => {
                blocker.proceed();
            },
            () => {
                blocker.reset();
            },
        );
    }, [blocker, confirmDiscard]);

    return { confirmDiscard };
}
